import type { BulkActionRequest, SsidBand } from '@simpleacs/shared';

/**
 * The bulk SSID form's own state rules, beside `bulk-wan-mode.ts` and for the
 * same reason: the page renders, this decides, and a test can hold it to the
 * one promise that matters — the form never BUILDS a request the boundary
 * would refuse.
 */

export type SsidRequest = Extract<BulkActionRequest, { action: 'ssid_set' | 'ssid_remove' }>;

/**
 * The bands this run addresses, with any interface chosen for a band that has
 * just been UNTICKED taken back out.
 *
 * Unticking used to set `bands` and leave `port5g` behind, and the two halves
 * of the form then disagreed: the Build gate asks "does every addressed band
 * have an interface" and said yes, while the boundary also asks the reverse —
 * "is any interface chosen for a band this run does not address" — and refused
 * the plan with a 400 whose field the operator could not see (2026-09-24, three
 * refused removals on the HS units).
 *
 * The state is what gets fixed rather than the rule. An interface chosen for a
 * band nobody is addressing is a choice that would do nothing, and the same
 * check is what stops a 5 GHz name landing on the subscriber's 2.4 GHz radio;
 * it earns its place. A form that quietly carries a dropped band's answer
 * forward does not.
 */
export function withBands<T extends SsidRequest>(request: T, bands: SsidBand[]): T {
  return {
    ...request,
    bands,
    port24: bands.includes('2.4G') ? request.port24 : null,
    port5g: bands.includes('5G') ? request.port5g : null,
  };
}
